/**
 * "+ Add block" gallery (Task 6). Every `BlockSection`'s "+ Add block" card
 * opens this shared modal, regardless of which section it was clicked from —
 * the picked category decides where the new block lands (blockSlotsForGroup
 * groups by category, not by click origin). Curated tiles come straight from
 * `BLOCK_TAXONOMY` so labels/emoji/descriptions never drift from the card
 * taxonomy; the dashed "Custom block…" tile is the escape hatch for anything
 * not in the curated list — its name is slugified at pick time
 * (`slugifyCategory`) so `onPick` always receives a final, storable category.
 */
import { useState } from 'react'
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalBody } from '@/components/ui/Modal'
import { BLOCK_TAXONOMY, type BlockGroup } from './blockTaxonomy'
import { slugifyCategory } from './slugifyCategory'
import type { BlockCategory } from '@/stores/composerStore'

interface Props {
  open: boolean
  onClose: () => void
  onPick: (category: string) => void
}

// Curated display order — groups related categories (rules together,
// examples together) rather than the taxonomy's declaration order.
const GALLERY_ORDER: BlockCategory[] = [
  'identity', 'trait', 'tone', 'behavioral',
  'constraints', 'never_forget', 'escalation',
  'domain', 'examples', 'do_examples', 'dont_examples',
  'output_format', 'freeform',
]

// Destination-section headers, in display order. Each tile is bucketed under
// the section its category actually renders into (BLOCK_TAXONOMY[k].group), so
// a tile's destination is obvious before picking — the block never "vanishes"
// into a section other than the one it was named under. Labels are name-free
// (the gallery has no persona name); `who`/identity is excluded (not addable).
const GALLERY_GROUPS: { key: Exclude<BlockGroup, 'who'>; emoji: string; label: string }[] = [
  { key: 'how', emoji: '🎨', label: 'Personality & behavior' },
  { key: 'rules', emoji: '🛡️', label: 'Rules & guardrails' },
  { key: 'know', emoji: '📚', label: 'Knowledge & examples' },
  { key: 'extra', emoji: '🧩', label: 'Formatting & extras' },
]

export function AddBlockGallery({ open, onClose, onPick }: Props) {
  const [customName, setCustomName] = useState('')
  const [showCustom, setShowCustom] = useState(false)

  const pick = (category: string) => {
    onPick(category)
    onClose()
    setShowCustom(false)
    setCustomName('')
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle>Add a block</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <p className="mb-3 text-sm text-muted-foreground">
            Pick a block type — each guides what to write and how it renders into the prompt.
          </p>
          <div className="space-y-4">
            {GALLERY_GROUPS.map((group) => {
              const categories = GALLERY_ORDER.filter(
                (k) => BLOCK_TAXONOMY[k].addable !== false && BLOCK_TAXONOMY[k].group === group.key,
              )
              if (categories.length === 0) return null
              return (
                <div key={group.key} data-testid={`gallery-group-${group.key}`}>
                  <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <span aria-hidden>{group.emoji}</span> {group.label}
                  </p>
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    {categories.map((k) => {
                      const meta = BLOCK_TAXONOMY[k]
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => pick(k)}
                          className="flex gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3 text-left transition hover:-translate-y-px hover:border-primary hover:bg-primary/10"
                        >
                          <span aria-hidden className="flex h-9 w-9 items-center justify-center rounded-lg bg-background text-lg">
                            {meta.emoji}
                          </span>
                          <span>
                            <span className="block text-sm font-semibold text-foreground">{meta.label}</span>
                            <span className="block text-xs text-muted-foreground">{meta.galleryDescription}</span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>

          {showCustom ? (
            <form
              className="mt-3 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                if (customName.trim()) pick(slugifyCategory(customName))
              }}
            >
              <input
                aria-label="Block name"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="e.g. Brand voice"
                className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                autoFocus
              />
              <button
                type="submit"
                disabled={!customName.trim()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                Add
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setShowCustom(true)}
              className="mt-3 flex w-full items-center gap-3 rounded-xl border border-dashed border-border p-3 text-left text-sm font-semibold text-primary hover:bg-primary/10"
            >
              <span aria-hidden className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-lg">
                ✏️
              </span>
              Custom block…<span className="font-normal text-muted-foreground">name your own</span>
            </button>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
